/**
 * Credits Hook - Real-time credit balances + billing info from Firestore
 */

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useEffectiveClientId } from './useImpersonation';

export function useCredits() {
  const effectiveClientId = useEffectiveClientId();
  const [balance, setBalance] = useState<number>(0);
  const [contactBalance, setContactBalance] = useState<number>(0);
  const [billingType, setBillingType] = useState<string>('per_call');
  const [platformPaidUntil, setPlatformPaidUntil] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!effectiveClientId) { setLoading(false); return; }

    const clientRef = doc(db, 'clients', effectiveClientId);
    const unsubscribe = onSnapshot(clientRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setBalance(data?.credits || 0);
        setContactBalance(data?.contactCredits || 0);
        setBillingType(data?.billingType || 'per_call');
        const ppu = data?.platformPaidUntil;
        if (ppu && typeof ppu.toDate === 'function') {
          setPlatformPaidUntil(ppu.toDate().getTime());
        } else if (ppu) {
          setPlatformPaidUntil(new Date(ppu).getTime());
        } else {
          setPlatformPaidUntil(null);
        }
      }
      setLoading(false);
    }, (error) => {
      console.error('Credits listener error:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [effectiveClientId]);

  const activeBalance = billingType === 'per_contact' ? contactBalance : balance;
  const platformActive = platformPaidUntil ? platformPaidUntil > Date.now() : false;

  return { balance, contactBalance, billingType, platformPaidUntil, platformActive, activeBalance, loading };
}
